/* Screen dispatcher
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 408-427.
 * The body below was byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 * ⚠ NO LONGER, SINCE 2026-10-04: `render({keep})` and `patchKeeping`
 * below — his walk of step 6, the keyboard that flipped to letters.
 */

import { isCol, isObs } from './catalog.js'
import { chipBody, hydrateBrands, isErr } from './community.js'
import { hydratePhotos } from './photos.js'
import { $, state } from './state.js'
import { vAccount, vLearn } from './views-account.js'
import { browserCard, vConfirm, vForgot, vNewPass, vNoProfile, vSignIn } from './views-auth.js'
import { vCommunity, vPublicNote, vPublicProfile } from './views-community.js'
import { vCatalog, vCollection, vDetail, vHolding, vPick } from './views-vault.js'

/* ⚠ THE FIELD BEING TYPED IN STAYS IN THE PAGE — his walk of step 6,
 * 2026-10-04: typing digits into a search box flipped his phone's keyboard
 * back to letters after every character. Measured: each keystroke rebuilt
 * `#app`, so the box was a NEW element every time, focused afresh, and a
 * freshly focused box opens the keyboard in letters. `render({keep})`
 * draws the same screen but leaves the element `keep` names exactly where
 * it is: every node around it is replaced by the new render, while it and
 * its ancestors are never detached — so focus, caret and the keyboard's
 * layout survive. It returns false (and draws the screen whole) when the
 * element is not the one focused, or the new render has none, or its depth
 * moved; the caller then re-focuses as before. */
function patchKeeping(a,html,sel){
  const live=document.activeElement;
  const tmp=document.createElement("div");tmp.innerHTML=html;
  const fresh=tmp.querySelector(sel);
  if(!live||live===a||!a.contains(live)||!live.matches(sel)||!fresh){a.innerHTML=html;return false;}
  const L=[],N=[];
  for(let x=live;x!==a;x=x.parentNode)L.unshift(x);
  for(let x=fresh;x!==tmp;x=x.parentNode)N.unshift(x);
  if(L.length!==N.length){a.innerHTML=html;return false;}
  let lp=a,np=tmp;
  for(let i=0;i<L.length;i++){
    const l=L[i],n=N[i];
    for(const c of [...lp.childNodes])if(c!==l)lp.removeChild(c);
    const kids=[...np.childNodes],at=kids.indexOf(n);
    for(const c of kids.slice(0,at))lp.insertBefore(c,l);
    for(const c of kids.slice(at+1))lp.appendChild(c);
    /* the kept nodes take the new render's attributes; the field keeps its own value */
    for(const x of [...l.attributes])if(!n.hasAttribute(x.name))l.removeAttribute(x.name);
    for(const x of [...n.attributes])if(x.name!=="value"&&l.getAttribute(x.name)!==x.value)l.setAttribute(x.name,x.value);
    lp=l;np=n;
  }
  return true;
}

/* ---------- the offline bar ---------- */
/* ⚠ THE OFFLINE BAR — the offline-add item, his ruling 2026-10-22. ONE sentence, his words exactly, written
 * into `#offbar` (index.html) and shown while `state.offline` is set: a thin bar under the header on every view,
 * never a toast, never over the vault. `main.js` sets the flag (the window's offline event, `navigator.onLine`
 * false at boot, a save that never reached the server) and clears it (the online event, the next successful
 * save). It says what is true — a change made now will NOT be saved — and promises nothing: there is no retry
 * and no copy on the phone, so a note added offline is still lost on a reload while offline, by design.
 * `search-check` H1–H6 drive it; `search-sabotage` SB85 and SB86 break one thing each. */
const OFFLINE_BAR="Offline — changes will not be saved.";
function offbar(){const el=$("offbar");if(!el)return;el.textContent=OFFLINE_BAR;el.classList.toggle("show",!!state.offline);}

/* ---------- the wide layouts ---------- */
/* ⚠ THE WIDE LAYOUTS — his rulings of 2026-10-23 (shape B at 1100px and up, A's capped two columns at 768–1099px,
 * phones unchanged) and of 2026-10-24 (landscape phones unchanged — the height floor; the vault alone as two panes;
 * + Add visible on a note in B; the placeholder's sentence). These are the SAME two queries as the two @media
 * blocks at the end of style.css, and `pubnote-check` §13 asserts the texts agree. They are read through
 * `matchMedia` and nothing else; with no `matchMedia` (jsdom, every gate written before these) both are false,
 * which is the phone. ⚠ A is CSS: this file only marks `#app` with the screen while A matches, so the stylesheet
 * can lay a screen out — and never on a phone, so the phone's DOM carries no new attribute. B is JS as well:
 * `#app` holds two panes (the vault list and, on a note, the note) because one view per `#app` cannot be two.
 * `state.screen` is untouched by the panes — `holding` still means a note is open and on screen, which is what
 * the card mark (`suggest.js`), the upload toasts and `hydratePhotos` read. */
const WIDE_A="(min-width:768px) and (min-height:500px)";
const WIDE_B="(min-width:1100px) and (min-height:500px)";
function wide(q){try{return typeof window!=="undefined"&&typeof window.matchMedia==="function"&&!!window.matchMedia(q).matches;}catch(e){return false;}}
/* the screen, for the stylesheet, while A matches (B's width is inside A's); off at phone widths */
function markLayout(){const a=$("app");if(!a)return;if(wide(WIDE_A)){if(a.dataset.screen!==state.screen)a.dataset.screen=state.screen;}else if("screen"in a.dataset)delete a.dataset.screen;}
/* two panes: B, on the vault or a note in it, over a vault that loaded and holds a note — an empty vault and the
 * "didn't load" panel stay one pane, as the sign-in screens and every other tab do */
function inPanes(){return wide(WIDE_B)&&(state.screen==="collection"||state.screen==="holding")&&!state.vaultUnread&&state.collection.length>0;}
/* ⚠ EACH PANE IS WRITTEN ONLY WHEN ITS OWN HTML CHANGED, and keeps its scroll when it is. A grade tap on the note
 * redraws the list (the card's grade and value), and a list that jumped to the top on every tap would be the
 * two-pane view not working. An unchanged pane is not touched at all: its scroll, a field focused in it and its
 * hydrated photographs stay. The note pane goes to the top when a DIFFERENT note opens, and only then. */
function renderPanes(a,keep,carry){
  let L=$("paneL"),R=$("paneR");
  if(!L||!R||L.parentNode!==a||R.parentNode!==a){a.innerHTML='<div class="paneL" id="paneL"></div><div class="paneR" id="paneR"></div>';a.scrollTop=0;L=$("paneL");R=$("paneR");R.addEventListener("scroll",()=>{R._ckTop=R.scrollTop;},{passive:true});}
  if(!a.classList.contains("panes"))a.classList.add("panes");
  const lh=browserCard()+vCollection(),rh=state.screen==="holding"?vHolding():vPick();
  const live=document.activeElement;let kept=false;
  for(const [p,h] of [[L,lh],[R,rh]]){
    if(p._ckHtml===h){if(keep&&live&&p.contains(live)&&live.matches(keep))kept=true;continue;}
    const top=p.scrollTop;
    const k=keep&&live&&p.contains(live)?patchKeeping(p,h,keep):(p.innerHTML=h,false);
    p._ckHtml=h;p.scrollTop=top;if(k)kept=true;
  }
  const key=state.screen==="holding"?"note:"+state.sel:"pick";
  if(R._ckKey!==key){R.scrollTop=carry!=null?carry:0;R._ckKey=key;}
  return kept;
}

/* ---------- screens ---------- */
function render(opts){
  const keep=opts&&opts.keep;
  $("screenlbl").textContent={collection:"Collection",catalog:"Resources",add:"Add a note",detail:"Note",holding:"Your note",community:"Community",pubprofile:"Collector",account:"Account",learn:"Learn"}[state.screen]||"";
  document.querySelectorAll("#nav [data-nav]").forEach(b=>b.classList.toggle("on",b.dataset.nav===state.screen));
  /* the add button hides while the "didn't load" panel shows — the offline-reload item, his go 2026-10-18 */
  $("fab").classList.toggle("show",state.screen==="collection"&&!state.vaultUnread);
  /* in B the vault list stays on screen with a note open: its tab stays lit and + Add stays (his ruling 6, 2026-10-24) */
  const panes=inPanes();
  if(panes&&state.screen==="holding"){$("fab").classList.add("show");const v=document.querySelector('#nav [data-nav="collection"]');if(v)v.classList.add("on");}
  offbar();
  markLayout();
  const a=$("app");
  /* a crossing of 1100px (`main.js`) keeps the open note's scroll: the same note, in its new place */
  const carry=opts&&opts.layout&&state.screen==="holding"?(($("paneR")&&$("paneR").parentNode===a)?($("paneR")._ckTop??$("paneR").scrollTop):a.scrollTop):null;
  if(panes){const k=renderPanes(a,keep,carry);hydratePhotos();hydrateBrands();return k;}
  if(a.classList.contains("panes"))a.classList.remove("panes");
  let html=null;
  if(state.screen==="collection")html=vCollection();
  else if(state.screen==="catalog")html=vCatalog();
  else if(state.screen==="add")html=vCatalog(true);
  else if(state.screen==="detail")html=vDetail();
  else if(state.screen==="holding")html=vHolding();
  else if(state.screen==="community")html=vCommunity();
  else if(state.screen==="pubprofile")html=vPublicProfile();
  else if(state.screen==="pubnote")html=vPublicNote();
  else if(state.screen==="account")html=vAccount();
  else if(state.screen==="learn")html=vLearn();
  else if(state.screen==="signin")html=vSignIn();
  else if(state.screen==="noprofile")html=vNoProfile();
  else if(state.screen==="confirm")html=vConfirm(); /* Phase 5 step 3, 2026-09-09 */
  else if(state.screen==="forgot")html=vForgot(); /* his ruling (94), 2026-10-17 */
  else if(state.screen==="newpass")html=vNewPass(); /* his ruling (94), 2026-10-17 */
  /* the browser card above the vault, after a confirmation or a reset in a browser — register 13448, (94) */
  if(html!==null&&state.screen==="collection")html=browserCard()+html;
  /* a kept field is mid-edit: the screen neither jumps to the top nor loses the field */
  /* ⚠ a screen this list does not name leaves `#app` as it was, as it always has */
  const kept=html===null?false:keep?patchKeeping(a,html,keep):(a.innerHTML=html,false);
  if(!kept)a.scrollTop=carry!=null?carry:0;
  hydratePhotos();hydrateBrands();
  return kept;
}

function denomInner(d){d=d||"—";if(d==="—")return '<b>—</b>';if(d.charAt(0)==="$")return '<small>$</small><b>'+d.slice(1)+'</b>';return '<b style="font-size:12px;letter-spacing:-.3px">'+d+'</b>';}
function chip(n){return `<div class="chip ${isErr(n)?"err":""}${isCol(n)?" col":""}${isObs(n)?" obs":""}">${chipBody(n)}</div>`;}

export {
  WIDE_A,
  WIDE_B,
  chip,
  denomInner,
  markLayout,
  offbar,
  render,
}
